import React, { useEffect, useRef, useState, useCallback } from 'react';
import Component from '../../components/Component';
import { BASE_URL } from '../../components/AppConfig';
import { sendAiMessage, getAiConversation, getAiPrompts, listAiConversations, reactToAiMessage } from '../../services/aiChatApi';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import SportsIcon from '@mui/icons-material/Sports';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddCommentIcon from '@mui/icons-material/AddComment';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ShareIcon from '@mui/icons-material/Share';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Render markdown for AI chat answers. Wraps MUI-friendly elements so the
 * rendered text matches the chat bubble typography (no raw ###/** leaking).
 */
function MarkdownAnswer({ children }) {
  const text = typeof children === 'string' ? children : String(children ?? '');
  return (
    <Box
      sx={{
        fontSize: '0.875rem',
        lineHeight: 1.5,
        wordBreak: 'break-word',
        '& h1, & h2, & h3': { fontSize: '1rem', fontWeight: 700, mt: 1, mb: 0.5 },
        '& h4, & h5, & h6': { fontSize: '0.9rem', fontWeight: 700, mt: 1, mb: 0.5 },
        '& p': { m: 0, mb: 0.75 },
        '& p:last-child': { mb: 0 },
        '& strong': { fontWeight: 700 },
        '& em': { fontStyle: 'italic' },
        '& ul, & ol': { pl: 2.5, m: 0, mb: 0.5 },
        '& li': { mb: 0.25 },
        '& code': {
          bgcolor: 'rgba(0,0,0,0.06)',
          px: 0.5,
          borderRadius: 0.5,
          fontSize: '0.85em',
          fontFamily: 'monospace',
        },
        '& pre': {
          bgcolor: 'rgba(0,0,0,0.06)',
          p: 1,
          borderRadius: 1,
          overflowX: 'auto',
          fontSize: '0.8rem',
        },
        '& blockquote': {
          borderLeft: '3px solid',
          borderColor: 'divider',
          pl: 1,
          ml: 0,
          color: 'text.secondary',
        },
        '& a': { color: 'primary.main' },
        '& table': { borderCollapse: 'collapse', my: 0.5 },
        '& th, & td': { border: '1px solid', borderColor: 'divider', p: 0.5, fontSize: '0.8rem' },
      }}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
    </Box>
  );
}

function getAuthHeaders() {
  const token = sessionStorage.getItem('sessionToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function AiChat() {
  const [matchOptions, setMatchOptions] = useState([]);
  const [matchSearchInput, setMatchSearchInput] = useState('');
  const [matchSearchLoading, setMatchSearchLoading] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [matchId, setMatchId] = useState('');

  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [prompts, setPrompts] = useState([]);
  const [followUps, setFollowUps] = useState([]);

  const [conversations, setConversations] = useState([]);
  const [convoLoading, setConvoLoading] = useState(false);

  const [rateLimit, setRateLimit] = useState(null);

  const messagesEndRef = useRef(null);
  const searchTimer = useRef(null);

  useEffect(() => {
    fetchMatches();
    loadConversations();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchMatches = async (search = '') => {
    setMatchSearchLoading(true);
    try {
      let url = `${BASE_URL}/admin/match-one-liner/matches?limit=50&offset=0`;
      const res = await fetch(url, {
        method: 'GET', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      });
      const data = await res.json().catch(() => ({}));
      let list = data.data?.matches ?? [];
      if (!Array.isArray(list)) list = [];
      list.sort((a, b) => {
        const da = a.date_start_ist ? new Date(a.date_start_ist).getTime() : 0;
        const db = b.date_start_ist ? new Date(b.date_start_ist).getTime() : 0;
        return db - da;
      });
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        list = list.filter((m) => {
          const fields = [String(m.match_id), m.title, m.short_title, m.teama__name, m.teama__short_name, m.teamb__name, m.teamb__short_name].map(f => (f || '').toLowerCase());
          return fields.some(f => f.includes(q));
        });
      }
      setMatchOptions(list);
    } catch {
      setMatchOptions([]);
    } finally {
      setMatchSearchLoading(false);
    }
  };

  const handleSearchInputChange = useCallback((e, value) => {
    setMatchSearchInput(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchMatches(value), 300);
  }, []);

  const loadConversations = async () => {
    setConvoLoading(true);
    try {
      const res = await listAiConversations(20, 0);
      setConversations(res.data?.conversations || []);
    } catch {
    } finally {
      setConvoLoading(false);
    }
  };

  // Load conversation via new endpoint
  const loadConversation = async (mid, sid = null) => {
    try {
      const res = await getAiConversation(mid, sid);
      const d = res.data;
      if (d.conversation?.id) setSessionId(d.conversation.id);
      if (d.messages) setMessages(d.messages);
      if (d.ui?.suggestions) setPrompts(d.ui.suggestions.map(s => typeof s === 'string' ? s : s.label || s));
      if (d.rate_limit) setRateLimit(d.rate_limit);
    } catch (err) {
      console.error('Failed to load conversation:', err);
    }
  };

  const handleMatchSelect = async (event, match) => {
    if (!match) return;
    setSelectedMatch(match);
    setMatchId(String(match.match_id));
    setMessages([]);
    setSessionId(null);
    setError('');
    setFollowUps([]);
    await loadConversation(match.match_id);
  };

  const resumeConversation = async (convo) => {
    setMatchId(String(convo.match_id));
    setSelectedMatch({ match_id: convo.match_id, title: convo.title || 'Match', teama__short_name: '', teamb__short_name: '' });
    setError('');
    setFollowUps([]);
    await loadConversation(convo.match_id, convo.id);
  };

  const newSession = () => {
    setMessages([]);
    setSessionId(null);
    setFollowUps([]);
    setError('');
    if (matchId) loadConversation(matchId);
  };

  const handleSend = async (text) => {
    const msg = (text || input).trim();
    if (!msg || !matchId) return;

    setInput('');
    setError('');
    setFollowUps([]);
    setMessages((prev) => [...prev, { id: `m_user_${Date.now()}`, role: 'user', text: msg, timestamp: Date.now() }]);
    setLoading(true);

    try {
      const res = await sendAiMessage(matchId, msg, sessionId);
      const d = res.data;
      if (!sessionId && d.session_id) setSessionId(d.session_id);

      setMessages((prev) => [...prev, {
        id: d.id,
        role: 'assistant',
        text: d.text,
        headline: d.headline,
        share_text: d.share_text,
        timestamp: d.timestamp || Date.now(),
        reactions: d.reactions || { liked: false, disliked: false },
        intent: d.intent,
        confidence_score: d.confidence_score,
      }]);

      if (d.follow_ups?.length > 0) setFollowUps(d.follow_ups);
      if (d.rate_limit) setRateLimit(d.rate_limit);
    } catch (err) {
      setError(err.message || 'Failed to send message');
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const handleReact = async (messageId, reaction) => {
    if (!sessionId) return;
    try {
      await reactToAiMessage(messageId, sessionId, reaction);
      setMessages((prev) => prev.map(m => {
        if (m.id === messageId) {
          return { ...m, reactions: { liked: reaction === 'like', disliked: reaction === 'dislike' } };
        }
        return m;
      }));
    } catch (err) {
      console.error('React failed:', err);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const getMatchLabel = (m) => {
    if (!m) return '';
    const date = m.date_start_ist ? new Date(m.date_start_ist).toLocaleDateString() : '';
    return `${m.match_id} — ${m.teama__short_name || '?'} vs ${m.teamb__short_name || '?'} — ${m.status_str || ''} — ${date}`;
  };

  const activeSuggestions = followUps.length > 0 ? followUps : prompts;

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <h3>Duggy AI Chat</h3>
                <p>Test the AI chatbot with any match</p>
              </div>
            </div>
          </div>

          <Grid container spacing={2}>
            {/* Left sidebar */}
            <Grid item xs={12} md={3}>
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>Select Match</Typography>
                  <Autocomplete
                    options={matchOptions}
                    getOptionLabel={getMatchLabel}
                    loading={matchSearchLoading}
                    onInputChange={handleSearchInputChange}
                    onChange={handleMatchSelect}
                    isOptionEqualToValue={(opt, val) => opt.match_id === val.match_id}
                    renderInput={(params) => (
                      <TextField {...params} size="small" placeholder="Search by ID, team, date..."
                        InputProps={{ ...params.InputProps, endAdornment: (<>{matchSearchLoading ? <CircularProgress size={18} /> : null}{params.InputProps.endAdornment}</>) }} />
                    )}
                    renderOption={(props, m) => (
                      <li {...props} key={m.match_id}>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {m.teama__short_name || '?'} vs {m.teamb__short_name || '?'}
                            <Chip label={m.status_str || '?'} size="small" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }}
                              color={m.status_str === 'Live' ? 'error' : m.status_str === 'Completed' ? 'success' : 'default'} />
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            #{m.match_id} &middot; {m.title} &middot; {m.date_start_ist ? new Date(m.date_start_ist).toLocaleDateString() : ''}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    noOptionsText="No matches found"
                    fullWidth
                  />
                </CardContent>
              </Card>

              {/* Rate limit info */}
              {rateLimit && (
                <Card sx={{ mb: 2 }}>
                  <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                    <Typography variant="caption" color="text.secondary">
                      Messages: {rateLimit.messages_used}/{rateLimit.daily_limit} &middot; Remaining: {rateLimit.messages_remaining}
                    </Typography>
                  </CardContent>
                </Card>
              )}

              {/* Past conversations */}
              <Card>
                <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, px: 1 }}>
                    <Typography variant="subtitle2">Past Chats</Typography>
                    <IconButton size="small" onClick={loadConversations}><RefreshIcon fontSize="small" /></IconButton>
                  </Box>
                  {convoLoading ? (
                    <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={20} /></Box>
                  ) : conversations.length === 0 ? (
                    <Typography variant="body2" sx={{ px: 1, color: 'text.secondary' }}>No past conversations</Typography>
                  ) : (
                    <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
                      {conversations.map((c, i) => (
                        <ListItem key={i} disablePadding>
                          <ListItemButton selected={sessionId === c.id} onClick={() => resumeConversation(c)} sx={{ borderRadius: 1 }}>
                            <ListItemText
                              primary={<Typography variant="body2" fontWeight={500}>Match {c.match_id}</Typography>}
                              secondary={<Typography variant="caption" color="text.secondary">{c.last_message?.text?.substring(0, 50) || 'No messages'}</Typography>}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Main chat area */}
            <Grid item xs={12} md={9}>
              <Card sx={{ height: '75vh', display: 'flex', flexDirection: 'column' }}>
                {/* Header */}
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SmartToyIcon color="primary" />
                      {selectedMatch
                        ? `${selectedMatch.teama__short_name || '?'} vs ${selectedMatch.teamb__short_name || '?'} (#${matchId})`
                        : 'Select a match to start'}
                    </Typography>
                    {sessionId && <Typography variant="caption" color="text.secondary">Session: {sessionId.substring(0, 8)}...</Typography>}
                  </Box>
                  {matchId && (
                    <Button size="small" variant="outlined" startIcon={<AddCommentIcon />} onClick={newSession}>New Chat</Button>
                  )}
                </Box>

                {/* Messages area */}
                <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                  {!matchId ? (
                    <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                      <SportsIcon sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
                      <Typography variant="h6">Search and select a match to start chatting</Typography>
                      <Typography variant="body2">Or select a past conversation from the sidebar</Typography>
                    </Box>
                  ) : messages.length === 0 && !loading ? (
                    <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                      <Typography variant="body1" gutterBottom>Ask anything about this match!</Typography>
                    </Box>
                  ) : null}

                  {messages.map((m, i) => (
                    <Box key={m.id || i} sx={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', mb: 2 }}>
                      <Paper elevation={1} sx={{
                        p: 1.5, maxWidth: '75%', borderRadius: 2,
                        bgcolor: m.role === 'user' ? 'primary.main' : 'grey.100',
                        color: m.role === 'user' ? 'primary.contrastText' : 'text.primary',
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                          {m.role === 'user'
                            ? <PersonIcon sx={{ fontSize: 16 }} />
                            : <SmartToyIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
                          <Typography variant="caption" fontWeight={600}>{m.role === 'user' ? 'You' : 'Duggy AI'}</Typography>
                          {m.intent && <Chip label={m.intent} size="small" sx={{ height: 18, fontSize: '0.65rem', ml: 0.5 }} />}
                          {m.confidence_score && m.role === 'assistant' && (
                            <Chip label={m.confidence_score} size="small" color={m.confidence_score === 'high' ? 'success' : m.confidence_score === 'low' ? 'warning' : 'default'}
                              sx={{ height: 18, fontSize: '0.65rem' }} />
                          )}
                        </Box>
                        {m.headline && <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>{m.headline}</Typography>}
                        {m.role === 'assistant'
                          ? <MarkdownAnswer>{m.text || m.content}</MarkdownAnswer>
                          : <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.text || m.content}</Typography>}

                        {/* Reactions + share for assistant messages */}
                        {m.role === 'assistant' && m.id && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, borderTop: '1px solid', borderColor: 'divider', pt: 0.5 }}>
                            <IconButton size="small" onClick={() => handleReact(m.id, m.reactions?.liked ? 'none' : 'like')}>
                              {m.reactions?.liked ? <ThumbUpIcon sx={{ fontSize: 16, color: 'primary.main' }} /> : <ThumbUpOutlinedIcon sx={{ fontSize: 16 }} />}
                            </IconButton>
                            <IconButton size="small" onClick={() => handleReact(m.id, m.reactions?.disliked ? 'none' : 'dislike')}>
                              {m.reactions?.disliked ? <ThumbDownIcon sx={{ fontSize: 16, color: 'error.main' }} /> : <ThumbDownOutlinedIcon sx={{ fontSize: 16 }} />}
                            </IconButton>
                            {m.share_text && (
                              <IconButton size="small" onClick={() => navigator.clipboard.writeText(m.share_text)}>
                                <ShareIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            )}
                          </Box>
                        )}
                      </Paper>
                    </Box>
                  ))}

                  {loading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <SmartToyIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">Thinking...</Typography>
                    </Box>
                  )}
                  <div ref={messagesEndRef} />
                </Box>

                {error && <Alert severity="error" sx={{ mx: 2, mb: 1 }} onClose={() => setError('')}>{error}</Alert>}

                {/* Suggestions / follow-ups */}
                {activeSuggestions.length > 0 && !loading && matchId && (
                  <Box sx={{ px: 2, pb: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {activeSuggestions.slice(0, 4).map((p, i) => (
                      <Chip key={i} label={typeof p === 'string' ? p : p.label || p} size="small" variant="outlined" color="primary" clickable
                        onClick={() => handleSend(typeof p === 'string' ? p : p.label || p)} />
                    ))}
                  </Box>
                )}

                {/* Input */}
                <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 1 }}>
                  <TextField fullWidth size="small"
                    placeholder={matchId ? 'Ask about this match...' : 'Select a match first'}
                    value={input} onChange={(e) => setInput(e.target.value)} onKeyPress={handleKeyPress}
                    disabled={!matchId || loading} multiline maxRows={3} inputProps={{ maxLength: 500 }} />
                  <IconButton color="primary" onClick={() => handleSend()} disabled={!matchId || !input.trim() || loading}>
                    <SendIcon />
                  </IconButton>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </div>
      </div>
    </Component>
  );
}

export default AiChat;
